import { roundUpDecimal, truncateDecimal } from "@/helpers/numberHelpers";

// Phase 2 optimization: Cache Intl.NumberFormat instances
// Creating these objects is expensive, so we reuse them for common formatting scenarios
const cachedFormatters = {
  integer: new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }),
  decimal: new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3
  })
};

/**
 * Fast number formatting using cached formatters.
 * Use this for high-volume formatting scenarios (e.g., formatting dataset rows).
 *
 * - For integers: formats with no decimal places
 * - For decimals: formats with up to 3 decimal places (no rounding)
 * - Returns string without commas for raw numeric comparison
 *
 * @param value - The numeric value to format.
 * @param type - Formatting type: "integer" (default) or "decimal".
 * @returns A formatted string without commas, or String(value) if invalid.
 */
export const formatNumberRaw = (
  value: number,
  type: "integer" | "decimal" = "integer"
): string => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return String(value);
  }

  const formatter = cachedFormatters[type];
  // Remove commas for raw numeric string (used in dataset table filtering)
  return formatter.format(value).replaceAll(",", "");
};

/**
 * Formats a number into a localized string.
 *
 * - If `type` is `"integer"`, the value is formatted with no decimals, not rounded.
 * - If `type` is `"decimal"`, the value is rounded **up** to 3 decimal places.
 * - The default locale is `"en-US"` but can be customized.
 *
 * @param value - The numeric value to format.
 * @param type - Formatting type: "integer" (default) or "decimal".
 * @param locale - Optional locale string (default: "en-US").
 * @returns A formatted string or original value if invalid.
 */
export const formatNumber = (
  value: number,
  type: "integer" | "decimal" = "integer",
  locale: string = "en-US",
  minimumFractionDigits: number = 0,
  maximumFractionDigits: number = 3,
  isRoundUp: boolean = true,
  isTruncate: boolean = false
): string | number => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return value;
  }

  const isInteger = type === "integer";
  let displayValue = value;

  if (!isInteger && isTruncate) {
    displayValue = truncateDecimal(value, maximumFractionDigits);
  } else if (!isInteger && isRoundUp) {
    displayValue = roundUpDecimal(value, maximumFractionDigits);
  }

  // Use cached formatter for default en-US locale with standard fraction digits
  if (
    locale === "en-US" &&
    minimumFractionDigits === 0 &&
    maximumFractionDigits === 3
  ) {
    return cachedFormatters[type].format(displayValue);
  }

  // Fall back to creating a new formatter for custom options
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: minimumFractionDigits,
    maximumFractionDigits: isInteger ? 0 : maximumFractionDigits
  }).format(displayValue);
};

/**
 * Parses a display string that may contain thousands separators (commas) back
 * into a number, e.g. "1,000" -> 1000, "234,000" -> 234000, "-1,234.5" -> -1234.5.
 *
 * Inputs like `<input>` values formatted with grouping commas would otherwise
 * become `NaN` through a bare `Number()` (and then serialize to `null` in JSON),
 * so callers that need the numeric value of a formatted field must go through
 * this helper. Returns `NaN` for non-numeric input so callers can guard.
 *
 * @param value - The formatted numeric string to parse.
 * @returns The parsed number, or `NaN` if not parseable.
 */
export const parseFormattedNumber = (value: string): number =>
  Number(value.replaceAll(",", ""));

/**
 * Formats a number into a compact string representation.
 * @param num - The number to format.
 * @returns A compact formatted string.
 * Example: 1500 -> "1.5K", 2000000 -> "2M"
 * Note: This uses the "compact" notation of Intl.NumberFormat, which may round the number.
 */
export const compactNumber = (num: number, decimal: number = 0): string => {
  return new Intl.NumberFormat("en", {
    notation: "compact",
    compactDisplay: "short",
    minimumFractionDigits: 0,
    maximumFractionDigits: decimal
  }).format(num);
};
